import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { AuthForm, type AuthMode } from './AuthForm'

interface AuthModalProps {
  open: boolean
  mode: AuthMode
  onOpenChange: (open: boolean) => void
  /** Unused now that the modal shows a unified "Log in or sign up" — kept for API stability. */
  onModeChange?: (mode: AuthMode) => void
}

export function AuthModal({ open, mode, onOpenChange }: AuthModalProps) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true))
    } else {
      setVisible(false)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className={`absolute inset-0 bg-neutral-200/60 backdrop-blur-[1px] transition-opacity duration-150 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={() => onOpenChange(false)}
      />
      <div
        className={`relative z-10 w-full max-w-[440px] rounded-3xl bg-card border border-neutral-300 shadow-[0_12px_30px_-12px_rgba(0,0,0,0.14)] px-8 py-9 transition-all duration-150 ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.97]'
        }`}
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-4 right-4 z-20 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <AuthForm
          mode={mode}
          redirectTo={`${window.location.origin}/auth`}
          trackLocation="modal"
        />
      </div>
    </div>
  )
}
